import React, { useEffect, useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ 
  id, 
  type, 
  title, 
  message, 
  duration = 6000, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300);
  }, [id, onClose]);

  useEffect(() => {
    setIsVisible(true);

    // Progress bar animation
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - (100 / (duration / 100));
      });
    }, 100);

    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [duration, handleClose]);

  const getStyles = () => {
    const configs = {
      success: {
        bg: 'bg-gradient-to-r from-green-500 to-emerald-600',
        icon: '✓',
        progressBg: 'bg-green-300',
      },
      error: {
        bg: 'bg-gradient-to-r from-red-500 to-rose-600',
        icon: '✕',
        progressBg: 'bg-red-300',
      },
      warning: {
        bg: 'bg-gradient-to-r from-amber-500 to-orange-600',
        icon: '⚠',
        progressBg: 'bg-amber-300',
      },
      info: {
        bg: 'bg-orange-500',
        icon: 'ℹ',
        progressBg: 'bg-orange-300',
      },
    };
    return configs[type];
  };

  const config = getStyles();

  return (
    <div 
      className={`
        transform transition-all duration-300 ease-out pointer-events-auto
        ${isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'}
      `}
    >
      <div className={`${config.bg} rounded-xl shadow-2xl overflow-hidden min-w-[320px] max-w-[420px]`}>
        {/* Main Content */}
      <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-white text-lg font-bold">{config.icon}</span>
          </div>
            
            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-base">
              {title}
            </p>
            {message && (
                <p className="mt-1 text-white/90 text-sm leading-relaxed">
                {message}
              </p>
            )}
          </div>
            
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="h-1 bg-black/10">
          <div 
            className={`h-full ${config.progressBg} transition-all duration-100 ease-linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default Toast; 
